import { apiWithoutAuth, type ApiResponse } from '@/shared';

import { financialCalendarPaths } from './paths';

import type {
  FinancialCalendarDailyCount,
  FinancialCalendarEvent,
  FinancialCalendarPage,
  FinancialEventCategory,
  FinancialEventImpact,
} from '../types';

/**
 * TRAD-2902 — 서버 `description`은 소스마다 언어가 섞여 있다(DART/BOK=한국어,
 * FRED/ALPHAVANTAGE/COINMARKETCAL=영문 — 근본원인 TRAD-2876, 언어 태그 없음).
 *
 * 🔴 [정정] 처음엔 "EN 로케일에서만 지운다"로 짰다가 되돌렸다 — iOS 커밋 `66ef7cf3`
 * (`strippingDescriptionForDetailNavigation`)를 직접 읽어보니 **로케일 분기 없이 전
 * 사용자에게서 지운다**(android도 route payload에 애초에 값을 안 실어 마찬가지). "3사가
 * 같아야 한다"는 이 팀 최다 사고 축이라 로케일 조건을 버리고 iOS/android와 동일하게
 * **전 로케일 블랭킷 제거**로 맞췄다.
 *
 * 🔴 왜 "소스가 영문이면 살리기"도 안 하는가: DART/BOK=한국어·FRED 등=영문이라는 매핑 자체는
 * 알지만, 그 매핑으로 "영문만 골라 보여주기"를 하면 web만 iOS/android보다 더 보여주는
 * 꼴이 되어 3사가 다시 갈린다. **이 결함은 web 코드로 완전히는 못 고친다** — 근본 해결은
 * 서버가 언어 태그를 내려주는 TRAD-2876이고, 그게 풀리면 TRAD-2873에서 3사가 같이 다시
 * 그린다. 지금 web이 하는 건 "한국어 원문 노출"을 막는 데까지다(iOS/android와 동급).
 *
 * `EventDetailPanel`/`Drawer`의 `event.description && (...)` 분기는 null을 그대로
 * 통과시키므로 뷰 쪽은 변경 불필요 — iOS도 표시 계산 로직(`descriptionDisplayText`)은 안
 * 지우고 데이터만 지웠다(TRAD-2876 해소 후 스트리핑만 걷어내면 되게).
 */
export const stripDescription = (events: FinancialCalendarEvent[]): FinancialCalendarEvent[] =>
  // `== null`(느슨한 비교)로 null과 undefined를 함께 no-op 처리 — 이미 없는 값이면 새 객체를
  // 만들지 않고 원본 참조를 그대로 돌려준다(테스트 픽스처처럼 description 필드 자체가 없는
  // 객체도 안전하게 통과, 불필요한 복제 없음).
  events.map(event => (event.description == null ? event : { ...event, description: null }));

export interface FetchFinancialCalendarParams {
  from: string; // YYYY-MM-DD (required)
  to: string; // YYYY-MM-DD (required)
  category?: FinancialEventCategory[]; // comma-separated multi-value; omit = all
  country?: string[]; // comma-separated multi-value; omit = all (incl. null)
  importance?: FinancialEventImpact[]; // comma-separated multi-value; omit = all (incl. null)
  page: number; // 1-based
  pageSize: number;
}

// TRAD-2452 — data.result가 없을 때(계약 위반·mock 응답 등) 안전한 빈 페이지로 폴백한다.
// board(findIdeas 등)가 이미 쓰는 패턴과 동일. 폴백 없이 `undefined`를 그대로 돌려주면
// `useFinancialCalendarInfiniteQuery`의 `getNextPageParam(lastPage=undefined, ...)`가
// `Cannot read properties of undefined (reading 'last')`로 크래시하고, react-query가
// 잡는 쿼리 에러가 아니라 **React 렌더 중 예외**라 라우터 errorElement가 페이지 전체를
// 교체한다 — PageMeta가 이미 그려 넣은 title/description까지 함께 사라진다(실측:
// prerender.mjs의 제네릭 API mock이 이 계약을 안 지켜 dev에 title 없는 페이지가 배포됨).
const EMPTY_CALENDAR_PAGE: FinancialCalendarPage = {
  content: [],
  last: true,
  totalElements: 0,
  totalPages: 0,
  number: 0,
  size: 0,
  numberOfElements: 0,
  first: true,
  empty: true,
};

export const fetchFinancialCalendarEvents = async (
  params: FetchFinancialCalendarParams,
): Promise<FinancialCalendarPage> => {
  const { category, country, importance, ...rest } = params;

  const queryParams: Record<string, string | number> = { ...rest };

  // Serialize multi-value filters as comma-separated strings (backend supports both forms)
  if (category && category.length > 0) queryParams.category = category.join(',');
  if (country && country.length > 0) queryParams.country = country.join(',');
  if (importance && importance.length > 0) queryParams.importance = importance.join(',');

  const { data } = await apiWithoutAuth.get<ApiResponse<FinancialCalendarPage>>(financialCalendarPaths.events, {
    params: queryParams,
  });
  const page = data.result;
  if (!page) return EMPTY_CALENDAR_PAGE;
  // TRAD-2902 — 전 로케일에서 description을 여기서 구조적으로 지운다(iOS/android와 동일).
  return { ...page, content: stripDescription(page.content) };
};

// TRAD-2071: 기본 노출(HIGH만) 정책의 "전체 M건" 고지용 날짜별 집계.
// /events(Page<>)와 의도적으로 분리된 별도 엔드포인트 — importance는 받지 않는다
// (total이 중요도 필터와 무관함을 API 시그니처 자체로 보장하기 위함).
export interface FetchFinancialCalendarDailyCountsParams {
  from: string; // YYYY-MM-DD (required)
  to: string; // YYYY-MM-DD (required)
  category?: FinancialEventCategory[]; // comma-separated multi-value; omit = all
  country?: string[]; // comma-separated multi-value; omit = all (incl. null)
}

export const fetchFinancialCalendarDailyCounts = async (
  params: FetchFinancialCalendarDailyCountsParams,
): Promise<FinancialCalendarDailyCount[]> => {
  const { category, country, ...rest } = params;

  const queryParams: Record<string, string> = { ...rest };

  if (category && category.length > 0) queryParams.category = category.join(',');
  if (country && country.length > 0) queryParams.country = country.join(',');

  const { data } = await apiWithoutAuth.get<ApiResponse<FinancialCalendarDailyCount[]>>(
    financialCalendarPaths.dailyCounts,
    { params: queryParams },
  );
  // TRAD-2452 — 위 fetchFinancialCalendarEvents와 같은 이유의 폴백(이 엔드포인트는 소비처가
  // `dailyCounts?.length` 등으로 이미 optional-safe라 크래시 사례는 없었지만, 계약 위반 시
  // 안전한 빈 배열로 떨어지는 게 일관적이다).
  return data.result ?? [];
};
